import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { jobsFrom, usePrint } from './PrintProgress';
import { API_URL } from '../config/api';

type PrintKind = 'logo';

type PrintSourceItem = { sku?: string | null; name: string; quantity: number };

type PrintSource = {
  orderName?: string | null;
  orderId?: string | null;
  packageNumber?: number | null;
  productCount?: number | null;
  items?: PrintSourceItem[];
  trackingNumber?: string | null;
  carrier?: string | null;
  packageType?: string | null;
};

type PrintJob = {
  id: string;
  document_type?: string | null;
  copies?: number | null;
  status?: string | null;
  error?: string | null;
  created_at: string;
  done_at?: string | null;
  format?: string | null;
  source?: PrintSource | null;
  reprint_of?: string | null;
  can_reprint?: boolean;
  has_preview?: boolean;
};

type JobDetail = {
  id: string;
  documentType: string;
  format: string;
  copies: number;
  status: string;
  error: string | null;
  createdAt: string;
  pickedAt: string | null;
  doneAt: string | null;
  printerName: string | null;
  operatorName: string | null;
  source: PrintSource | null;
  reprintOf: string | null;
  previewUrl: string | null;
  canReprint: boolean;
};

const DOC_LABELS: Record<string, string> = {
  product_labels: 'Etykiety produktowe',
  packing_list: 'Lista pakowania',
  shipping_label_a6: 'Etykieta kurierska',
  shipping_label_80mm: 'Etykieta kurierska 80 mm',
  goodspeed_label: 'Etykieta GoodSpeed',
  logo_label: 'Etykieta logo',
  reports: 'Raport produktowy',
  stock_pick: 'Lista do dobrania',
  stock_summary: 'Stan magazynu',
  test: 'Wydruk testowy',
};

const STATUS_META: Record<string, { label: string; icon: string; pill: string }> = {
  queued: { label: 'W kolejce', icon: 'clock', pill: 'pill-warn' },
  picked: { label: 'Pobrano', icon: 'clock', pill: 'pill-info' },
  printing: { label: 'Drukowanie', icon: 'printer', pill: 'pill-info' },
  done: { label: 'Wydrukowano', icon: 'check', pill: 'pill-ok' },
  error: { label: 'Błąd', icon: 'alert-triangle', pill: 'pill-err' },
  cancelled: { label: 'Anulowano', icon: 'x-circle', pill: 'pill-neutral' },
};

/* Dokumenty A4 (liczone w stronach, nie etykietach). */
const PAGE_DOCS = ['packing_list', 'reports', 'stock_pick', 'stock_summary'];

function jobLabel(documentType?: string | null): string {
  return DOC_LABELS[documentType || ''] || documentType || 'Wydruk';
}

function statusMeta(status?: string | null) {
  return STATUS_META[status || ''] || { label: status || 'Nieznany', icon: 'clock', pill: 'pill-neutral' };
}

// Odmiana „produkt" (1 / 2–4 / 5+) — np. „1 produkt", „3 produkty", „5 produktów".
function plProducts(n: number): string {
  const abs = Math.abs(n);
  const t = abs % 10;
  const h = abs % 100;
  if (abs === 1) return 'produkt';
  if (t >= 2 && t <= 4 && (h < 10 || h >= 20)) return 'produkty';
  return 'produktów';
}

// Mały druczek „skąd wziął się ten wydruk": z zamówienia #XXXX · Paczka 1 · 3 produkty.
function sourceSummary(s?: PrintSource | null): string | null {
  if (!s) return null;
  const parts: string[] = [];
  if (s.orderName) parts.push(`z zamówienia ${s.orderName}`);
  if (s.packageNumber != null) parts.push(`Paczka ${s.packageNumber}`);
  if (s.productCount != null) parts.push(`${s.productCount} ${plProducts(s.productCount)}`);
  return parts.length ? parts.join(' · ') : null;
}

// Odmiana „kopia" (1 / 2–4 / 5+) — np. „1 kopia", „3 kopie", „20 kopii".
function plCopies(n: number): string {
  const t = n % 10;
  const h = n % 100;
  if (n === 1) return 'kopia';
  if (t >= 2 && t <= 4 && (h < 10 || h >= 20)) return 'kopie';
  return 'kopii';
}

// Tyle przyjmuje API (/api/printing/print-logo-label przycina do 50) — suwak nie może obiecać więcej.
const MAX_LOGO_COPIES = 50;

function CopiesSheet({ onClose, onPrinted }: { kind: PrintKind; onClose: () => void; onPrinted: () => void }) {
  const { startPrint } = usePrint();
  const [copies, setCopies] = useState(1);

  function print() {
    onClose();
    startPrint({
      title: 'Etykieta logo',
      subtitle: `${copies} ${plCopies(copies)}`,
      run: async () => {
        const r = await axios.post(`${API_URL}/api/printing/print-logo-label`, { copies });
        onPrinted();
        return jobsFrom(r.data);
      },
    });
  }

  return (
    <div className="scrim center" onClick={onClose}>
      <div className="sheet dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Etykieta logo</h3>
        <p className="sub">Kopie: 1–{MAX_LOGO_COPIES}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <button className="icon-btn" onClick={() => setCopies((c) => Math.max(1, c - 1))}><Icon name="minus" size={18} /></button>
          <input type="range" min={1} max={MAX_LOGO_COPIES} value={copies} style={{ flex: 1 }} onChange={(e) => setCopies(+e.target.value)} />
          <button className="icon-btn" onClick={() => setCopies((c) => Math.min(MAX_LOGO_COPIES, c + 1))}><Icon name="plus" size={18} /></button>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28, width: 56, textAlign: 'right' }} className="tnum">{copies}</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="big-btn bb-ghost" style={{ flex: 1 }} onClick={onClose}>Anuluj</button>
          <button className="big-btn bb-primary" style={{ flex: 1 }} onClick={print}>
            <Icon name="printer" size={20} /> Drukuj
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="jd-row">
      <span className="jd-k">{label}</span>
      <span className="jd-v">{children}</span>
    </div>
  );
}

type ReprintTarget = { id: string; documentType?: string | null; source?: PrintSource | null };

function JobDetailSheet({ jobId, onClose, onReprint }: {
  jobId: string;
  onClose: () => void;
  onReprint: (job: ReprintTarget) => void;
}) {
  const { data, isLoading, isError } = useQuery<JobDetail>({
    queryKey: ['kiosk-print-job', jobId],
    queryFn: async () => (await axios.get(`${API_URL}/api/printing/jobs/${jobId}`)).data,
  });

  const src = data?.source || null;
  const items = src?.items || [];
  const meta = statusMeta(data?.status);

  return (
    <div className="scrim center" onClick={onClose}>
      <div className="sheet" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h3>{data ? jobLabel(data.documentType) : 'Szczegóły wydruku'}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Zamknij"><Icon name="x" size={20} /></button>
        </div>

        {isLoading && <div className="muted" style={{ padding: '18px 0' }}>Ładowanie szczegółów…</div>}
        {isError && <div className="job-err" style={{ padding: '18px 0' }}>Nie udało się pobrać szczegółów zadania.</div>}

        {data && (
          <>
            <div style={{ marginBottom: 14 }}>
              <span className={`pill ${meta.pill}`}><Icon name={meta.icon} size={13} /> {meta.label}</span>
              {data.reprintOf && <span className="pill pill-neutral" style={{ marginLeft: 8 }}><Icon name="rotate-ccw" size={13} /> ponowny druk</span>}
            </div>

            <div className="jd-grid">
              {src?.orderName && <DetailRow label="Zamówienie">{src.orderName}</DetailRow>}
              {src?.packageNumber != null && <DetailRow label="Paczka">{src.packageNumber}</DetailRow>}
              {src?.productCount != null && <DetailRow label="Produkty">{src.productCount} {plProducts(src.productCount)}</DetailRow>}
              {src?.packageType && <DetailRow label="Opakowanie">{src.packageType}</DetailRow>}
              {src?.trackingNumber && <DetailRow label="Nr śledzenia"><span className="mono">{src.trackingNumber}</span></DetailRow>}
              {src?.carrier && <DetailRow label="Kurier">{src.carrier}</DetailRow>}
              <DetailRow label="Drukarka">{data.printerName || '—'}</DetailRow>
              <DetailRow label="Format">{data.format}{data.copies > 1 ? ` · ${data.copies}×` : ''}</DetailRow>
              {data.operatorName && <DetailRow label="Operator">{data.operatorName}</DetailRow>}
              <DetailRow label="Utworzono">{new Date(data.createdAt).toLocaleString('pl-PL')}</DetailRow>
              {data.doneAt && <DetailRow label="Wydrukowano">{new Date(data.doneAt).toLocaleString('pl-PL')}</DetailRow>}
              {data.error && <DetailRow label="Błąd"><span className="job-err">{data.error}</span></DetailRow>}
            </div>

            {items.length > 0 && (
              <div className="jd-items">
                <div className="jd-items-head">Zawartość paczki</div>
                {items.map((it, i) => (
                  <div className="jd-item" key={`${it.sku || it.name}-${i}`}>
                    <span className="jd-item-qty">{it.quantity}×</span>
                    <span className="jd-item-name">{it.name}</span>
                    {it.sku && <span className="jd-item-sku mono">{it.sku}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="jd-preview-head">Podgląd etykiety</div>
            {data.previewUrl ? (
              <>
                <iframe className="label-preview" src={data.previewUrl} title="Podgląd etykiety" />
                <a className="muted" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }} href={data.previewUrl} target="_blank" rel="noreferrer">
                  <Icon name="external-link" size={14} /> Otwórz w nowej karcie
                </a>
              </>
            ) : (
              <div className="muted" style={{ fontSize: 13, padding: '10px 0' }}>
                Podgląd niedostępny dla tego zadania (etykieta termiczna lub tekstowa).
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="big-btn bb-ghost" style={{ flex: 1 }} onClick={onClose}>Zamknij</button>
              <button
                className="big-btn bb-primary"
                style={{ flex: 1 }}
                disabled={!data.canReprint}
                onClick={() => onReprint({ id: data.id, documentType: data.documentType, source: data.source })}
              >
                <Icon name="rotate-ccw" size={20} /> Ponów druk
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function PrintsScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { startPrint } = usePrint();
  const [sheet, setSheet] = useState<PrintKind | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data: reports } = useQuery<{ history: PrintJob[] }>({
    queryKey: ['kiosk-report-history'],
    queryFn: async () => (await axios.get(`${API_URL}/api/printing/report-history?limit=10`)).data,
    refetchInterval: 10000,
  });

  const refreshJobs = () => qc.invalidateQueries({ queryKey: ['kiosk-report-history'] });

  // „Ponów druk" — ten sam zapisany payload na tę samą drukarkę, przez ekran drukowania.
  function reprint(job: ReprintTarget) {
    setDetailId(null);
    startPrint({
      title: jobLabel(job.documentType),
      subtitle: sourceSummary(job.source) || 'Ponowny wydruk',
      unit: PAGE_DOCS.indexOf(job.documentType || '') >= 0 ? 'page' : 'label',
      run: async () => {
        const r = await axios.post(`${API_URL}/api/printing/jobs/${job.id}/reprint`);
        refreshJobs();
        return jobsFrom(r.data);
      },
      onClose: refreshJobs,
    });
  }

  return (
    <div className="screen">
      <div className="k-header">
        <button className="back" onClick={() => navigate('/packing-station/dashboard')}><Icon name="arrow-left" size={20} /></button>
        <div><div className="k-title">Wydruki</div><div className="k-sub">Status zadań · realtime</div></div>
      </div>
      <div className="scrollable section-pad" style={{ flex: 1 }}>
        <div className="action-grid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 18 }}>
          <button className="action-tile" onClick={() => setSheet('logo')}>
            <div className="ico"><Icon name="image" size={24} /></div>
            <div><div className="at-title" style={{ fontSize: 17 }}>Etykieta logo</div><div className="at-sub">Kopie: 1–{MAX_LOGO_COPIES}</div></div>
          </button>
        </div>
        <div className="section-title">Ostatnie zadania</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(reports?.history || []).map((j) => {
            const meta = statusMeta(j.status);
            const summary = sourceSummary(j.source);
            return (
              <div key={j.id} className="pkg-card job-card">
                <div className="job-row">
                  <div className="job-info">
                    <div className="job-title">
                      {jobLabel(j.document_type)}
                      {j.copies && j.copies > 1 ? ` · ${j.copies}×` : ''}
                    </div>
                    <div className="pmeta">{new Date(j.created_at).toLocaleString('pl-PL')}</div>
                    {summary && (
                      <div className="job-src">
                        {summary}
                        {j.reprint_of ? ' · ponowny druk' : ''}
                      </div>
                    )}
                    {j.status === 'error' && j.error && <div className="job-err">{j.error}</div>}
                  </div>
                  <span className={`pill ${meta.pill}`}>
                    <Icon name={meta.icon} size={13} /> {meta.label}
                  </span>
                </div>
                <div className="job-actions">
                  <button className="job-btn" onClick={() => setDetailId(j.id)}>
                    <Icon name="list" size={15} /> Szczegóły
                  </button>
                  <button
                    className="job-btn primary"
                    disabled={j.can_reprint === false}
                    onClick={() => reprint({ id: j.id, documentType: j.document_type, source: j.source })}
                  >
                    <Icon name="rotate-ccw" size={15} /> Ponów druk
                  </button>
                </div>
              </div>
            );
          })}
          {!(reports?.history || []).length && <div className="muted" style={{ fontSize: 13 }}>Brak ostatnich zadań druku.</div>}
        </div>
      </div>

      {sheet && <CopiesSheet kind={sheet} onClose={() => setSheet(null)} onPrinted={refreshJobs} />}
      {detailId && <JobDetailSheet jobId={detailId} onReprint={reprint} onClose={() => setDetailId(null)} />}
    </div>
  );
}
